import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader, Btn, Card, Badge, Spinner, useToast } from '../components/shared/UI';
import { useT } from '../i18n';
import api from '../utils/api';
import { CASE_STATUS_COLORS, CASE_PRIORITY_COLORS, useAttachments, AttachmentPicker } from './Support';

export function CaseThread({ messages, meLabel, themLabel, viewer = 'user' }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {messages.map((m, i) => {
        const mine = m.author === viewer;
        return (
          <div key={m._id || i} style={{
            alignSelf: mine ? 'flex-end' : 'flex-start',
            maxWidth: 'min(560px, 92%)',
            background: m.internal ? 'rgba(245,158,11,0.10)'
              : mine ? 'rgba(46,117,182,0.12)' : 'var(--hover-bg)',
            border: `1px solid ${m.internal ? 'rgba(245,158,11,0.4)' : 'var(--border-light)'}`,
            borderRadius: 12, padding: '10px 14px',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-subtle)', marginBottom: 4 }}>
              {m.authorName || (mine ? meLabel : themLabel)}
              {m.internal && ' · internal note'}
              <span style={{ fontWeight: 400, marginInlineStart: 8 }}>{new Date(m.createdAt).toLocaleString()}</span>
            </div>
            {m.body && (
              <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.55, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                {m.body}
              </div>
            )}
            {(m.attachments || []).length > 0 && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                {m.attachments.map((a, j) => (
                  <img key={j} src={a} alt="" onClick={() => window.open(a, '_blank')}
                    style={{ maxHeight: 140, maxWidth: 220, borderRadius: 8, border: '1px solid var(--border)', cursor: 'zoom-in' }} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function SupportCaseView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useT();
  const { show, ToastContainer } = useToast();
  const [sc, setSc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const att = useAttachments(show, t);

  useEffect(() => {
    api.get(`/support/${id}`).then(r => setSc(r.data))
      .catch(() => navigate('/support'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const send = async e => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post(`/support/${id}/messages`, { body: reply, attachments: att.attachments });
      setSc(data); setReply(''); att.clear();
    } catch (err) {
      show(err.response?.data?.message || t('support.sendFailed'), 'error');
    } finally { setSending(false); }
  };

  const act = async action => {
    try {
      const { data } = await api.post(`/support/${id}/${action}`);
      setSc(data);
      show(action === 'close' ? t('support.closedMsg') : t('support.reopenedMsg'));
    } catch (err) {
      show(err.response?.data?.message || t('support.sendFailed'), 'error');
    }
  };

  if (loading || !sc) return <Spinner />;
  const closed = sc.status === 'closed';

  return (
    <div>
      <ToastContainer />
      <PageHeader
        title={sc.subject}
        subtitle={`${sc.caseNumber} · ${t(`support.cat.${sc.category}`)}`}
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Badge label={t(`support.prio.${sc.priority}`)} color={CASE_PRIORITY_COLORS[sc.priority]} />
            <Badge label={t(`support.status.${sc.status}`)} color={CASE_STATUS_COLORS[sc.status]} />
            <Btn size="sm" variant="ghost" onClick={() => navigate('/support')}>← {t('support.allCases')}</Btn>
          </div>
        } />

      {sc.status === 'resolved' && (
        <Card style={{ marginBottom: 16, border: '1px solid rgba(16,185,129,0.4)', background: 'rgba(16,185,129,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 240px', fontSize: 13, color: 'var(--text)' }}>
              ✅ <b>{t('support.resolvedTitle')}</b> — {t('support.resolvedAsk')}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn size="sm" variant="success" onClick={() => act('close')}>{t('support.confirmClose')}</Btn>
              <Btn size="sm" variant="outline" onClick={() => act('reopen')}>{t('support.reopen')}</Btn>
            </div>
          </div>
        </Card>
      )}

      <Card style={{ marginBottom: 16 }}>
        <CaseThread messages={sc.messages || []} meLabel={t('support.you')} themLabel={t('support.supportTeam')} />
      </Card>

      {closed ? (
        <Card style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          {t('support.caseClosed')}{' '}
          <Btn size="sm" variant="ghost" onClick={() => navigate('/support')}>{t('support.newCase')}</Btn>
        </Card>
      ) : (
        <Card>
          <form onSubmit={send} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <textarea value={reply} onChange={e => setReply(e.target.value)} rows={3} required={att.attachments.length === 0}
              placeholder={t('support.replyPh')}
              style={{
                width: '100%', padding: '10px 12px', fontSize: 13, fontFamily: 'inherit',
                border: '1px solid var(--border)', borderRadius: 8, resize: 'vertical',
                background: 'var(--input-bg)', color: 'var(--text)', outline: 'none',
              }} />
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <AttachmentPicker att={att} t={t} />
              <Btn variant="primary" type="submit" disabled={sending}>
                {sending ? t('common.saving') : t('support.send')}
              </Btn>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
